import { SchedulerPackagingRoutesRowType } from './Scheduler.types'
import { parsePeriods } from './RulesWorkbench.timeline'

/**
 * "Specific Time" on the demand requirements table: a product that may only
 * be packed inside the windows the planner marks. The engine enforces it
 * (`product_allowed_windows`); this file is the Portal half, the check made
 * when the windows are saved that they are long enough to make the demand.
 */

/** The service stores booleans as text, and 'False' is truthy. */
export const asBool = (v: unknown): boolean => {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v !== 0
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase()
    return s === 'true' || s === '1' || s === 'yes'
  }
  return false
}

const num = (v: unknown): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/** PR as the engine reads it: blank is 100%, anything over 1 a percentage. */
const prFactor = (v: unknown): number => {
  if (v === null || v === undefined || String(v).trim() === '') return 1
  const n = Number(String(v).replace('%', ''))
  if (!Number.isFinite(n) || n <= 0) return 1
  return n > 1 ? n / 100 : n
}

/** Total hours covered by the windows, counting an overlap once. */
export const windowHours = (value: string | null | undefined): number => {
  const spans = parsePeriods(value)
    .filter(([s, e]) => e > s)
    .sort((a, b) => a[0] - b[0])
  let total = 0
  let reach = -Infinity
  for (const [s, e] of spans) {
    const from = Math.max(s, reach)
    if (e > from) total += e - from
    reach = Math.max(reach, e)
  }
  return total
}

/**
 * Fastest rate, in product units per hour, at which each product can be
 * packed on one line: the line's run rate times its PR, capped by what the
 * route's kitchen can supply (`kitchenRunRate / conversionFactor`, the same
 * cap the decoder applies). Keyed by productCode; the conversion factor comes
 * from the demand row, so it is applied later in `hoursNeeded`.
 */
export type ProductRates = Record<
  string,
  { line: string; lineRate: number; kitchenRate: number }[]
>

export const productRatesFromRoutes = (
  routes: SchedulerPackagingRoutesRowType[]
): ProductRates => {
  const out: ProductRates = {}
  for (const r of routes) {
    if (!r?.productCode) continue
    const row = r as unknown as Record<string, unknown>
    const kitchenRate = num(r.kitchenRunRate)
    for (const i of [1, 2, 3, 4]) {
      const line = row[`packingLine${i}`]
      const rate = num(row[`lineRunRate${i}`])
      if (!line || rate <= 0) continue
      ;(out[String(r.productCode)] ??= []).push({
        line: String(line),
        lineRate: rate * prFactor(row[`pr${i}`]),
        kitchenRate,
      })
    }
  }
  return out
}

/** Hours to make `quantity` on the product's fastest line, or null when no
 *  route gives it a rate (nothing to check against). */
export const hoursNeeded = (
  rates: ProductRates,
  productCode: string,
  quantity: number,
  conversionFactor: number
): { hours: number; line: string; rate: number } | null => {
  const conv = conversionFactor > 0 ? conversionFactor : 1
  let best: { line: string; rate: number } | null = null
  for (const r of rates[productCode] ?? []) {
    const rate =
      r.kitchenRate > 0
        ? Math.min(r.lineRate, r.kitchenRate / conv)
        : r.lineRate
    if (rate > 0 && (!best || rate > best.rate)) best = { line: r.line, rate }
  }
  if (!best || quantity <= 0) return null
  return {
    hours: Math.ceil(quantity / best.rate),
    line: best.line,
    rate: best.rate,
  }
}

/** The warning shown before saving windows too short for the demand, or null. */
export const shortWindowWarning = (
  rates: ProductRates,
  row: {
    productCode: string
    productName?: string
    quantity: unknown
    quantityStillToMake?: unknown
    conversionFactor?: unknown
  },
  windows: string
): string | null => {
  // A stated remainder is what the engine plans against, as it does here.
  const still = num(row.quantityStillToMake)
  const quantity = still > 0 ? still : num(row.quantity)
  const need = hoursNeeded(
    rates,
    String(row.productCode),
    quantity,
    num(row.conversionFactor)
  )
  if (!need) return null
  const allowed = windowHours(windows)
  if (allowed >= need.hours) return null
  const name = row.productName ? ` (${row.productName})` : ''
  return (
    `The time windows for ${row.productCode}${name} are too short. ` +
    `Making ${quantity.toLocaleString()} on its fastest line, ${need.line} ` +
    `at ${Math.round(need.rate).toLocaleString()} an hour, needs ${need.hours}h, ` +
    `but the windows allow only ${allowed}h. ` +
    `Whatever does not fit will be left as unmet demand.`
  )
}

/** A value typed in an hours column, or null when the cell is blank. */
const hourOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || String(v).trim() === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export type TimeWindowShortfall = {
  quantity: number
  from: number
  to: number
  allowedHours: number
  neededHours: number
  line: string
  rate: number
  /** What the run keeps: the engine trims demand to this and reports the
   *  rest as unmet (`preprocess::trim_demand_to_time_window`). */
  keep: number
}

/**
 * Don't Start Before / Finish Before too close together for the demand on the
 * product's fastest line, or null. A blank Don't Start Before is hour 0, as
 * the engine reads it, so only a row with a Finish Before is judged. Specific
 * Time rows are left to their own windows check. The engine makes the same
 * estimate and trims the demand to `keep`, so this is what the planner is
 * agreeing to when they save anyway.
 */
export const timeWindowShortfall = (
  rates: ProductRates,
  row: {
    productCode: string
    quantity: unknown
    quantityStillToMake?: unknown
    conversionFactor?: unknown
    dontStartBefore?: unknown
    finishBefore?: unknown
    specificTime?: unknown
  }
): TimeWindowShortfall | null => {
  const to = hourOrNull(row.finishBefore)
  if (to === null || asBool(row.specificTime)) return null
  const from = hourOrNull(row.dontStartBefore) ?? 0
  const allowedHours = to - from
  if (allowedHours <= 0) return null
  const still = num(row.quantityStillToMake)
  const quantity = still > 0 ? still : num(row.quantity)
  const need = hoursNeeded(
    rates,
    String(row.productCode),
    quantity,
    num(row.conversionFactor)
  )
  if (!need || need.hours <= allowedHours) return null
  return {
    quantity,
    from,
    to,
    allowedHours,
    neededHours: need.hours,
    line: need.line,
    rate: need.rate,
    keep: Math.floor(allowedHours * need.rate),
  }
}
